/**
 * TuningView — brief-synthesis prompt editor + dry-run simulate.
 *
 * Edit the prompt (org- or client-scoped), simulate the briefs it would
 * produce for the client's current corpus WITHOUT persisting, then Save
 * once you're happy. Backed by /api/v1/organizations/{org}/creative-review.
 */
import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, Play, Save, RotateCcw, Trash2 } from 'lucide-react';
import {
  creativeReviewApi,
  type BriefPromptResponse,
  type CreativeReviewPreviewResponse,
} from '@/services/api';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useFetch } from '@/components/pdo/utils';

// Mirror app/strategic_rebuild/brief_prompt_config.py:_REQUIRED_PLACEHOLDERS
const REQUIRED = [
  'n_creatives',
  'pdo_concepts',
  'hook_types',
  'angle_archetypes',
  'emotion_profile',
  'review_excerpts',
  'performance_summary',
];

function missingPlaceholders(t: string): string[] {
  return REQUIRED.filter((p) => !t.includes(`{${p}}`));
}

export function TuningView({
  orgUid,
  clientUid,
}: {
  orgUid: string;
  clientUid: string;
}) {
  const [scope, setScope] = useState<'org' | 'client'>('client');
  const [draft, setDraft] = useState<string>('');
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const scopeClient = scope === 'client' ? clientUid : null;

  const cfgQuery = useFetch<BriefPromptResponse>(
    () => creativeReviewApi.getBriefPrompt(orgUid, scopeClient),
    [orgUid, scopeClient],
  );

  // Seed the editor from the effective template whenever the scope/config
  // (re)loads. User edits take over until the next scope switch.
  useEffect(() => {
    if (cfgQuery.data) setDraft(cfgQuery.data.effective_template);
    setStatus(null);
  }, [cfgQuery.data]);

  const missing = missingPlaceholders(draft);
  const valid = draft.trim().length > 0 && missing.length === 0;
  const dirty = !!cfgQuery.data && draft !== cfgQuery.data.effective_template;

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    setStatus(null);
    try {
      await creativeReviewApi.putBriefPrompt(orgUid, {
        template: draft,
        client_uid: scopeClient,
        updated_by: 'admin-ui',
      });
      setStatus(`Saved ${scope}-level prompt override.`);
      cfgQuery.refetch();
    } catch (e) {
      setStatus(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const removeOverride = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await creativeReviewApi.deleteBriefPrompt(orgUid, scopeClient);
      setStatus(`Removed ${scope}-level override — reverted to fallback.`);
      cfgQuery.refetch();
    } catch (e) {
      setStatus(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">
            Brief synthesis prompt
          </h2>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
            {(['client', 'org'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={clsx(
                  'px-3 py-1.5',
                  scope === s
                    ? 'bg-brand-teal text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {s === 'client' ? `Client (${clientUid})` : 'Organization'}
              </button>
            ))}
          </div>
        </div>

        {cfgQuery.data && (
          <p className="text-xs text-slate-500">
            Effective source:{' '}
            <span className="font-mono text-slate-700">
              {cfgQuery.data.source}
            </span>
          </p>
        )}
        {cfgQuery.error && (
          <ErrorBanner error={cfgQuery.error} onRetry={cfgQuery.refetch} />
        )}

        <textarea
          rows={20}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
          className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-lg focus:ring-1 focus:ring-brand-teal/30 focus:outline-none"
        />

        {missing.length > 0 && (
          <p className="text-xs text-danger font-mono">
            Missing required placeholders: {missing.map((m) => `{${m}}`).join(' ')}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={!valid || !dirty || saving}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-brand-teal text-white disabled:opacity-40"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            Save {scope} override
          </button>
          <button
            onClick={() =>
              cfgQuery.data && setDraft(cfgQuery.data.platform_default)
            }
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Load platform default
          </button>
          <button
            onClick={removeOverride}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 text-danger hover:bg-red-50 disabled:opacity-40"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete {scope} override
          </button>
        </div>
        {status && (
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            {status}
          </p>
        )}
      </section>

      <SimulatePanel
        orgUid={orgUid}
        clientUid={clientUid}
        draft={draft}
        draftValid={valid}
      />
    </div>
  );
}

function SimulatePanel({
  orgUid,
  clientUid,
  draft,
  draftValid,
}: {
  orgUid: string;
  clientUid: string;
  draft: string;
  draftValid: boolean;
}) {
  const [sample, setSample] = useState(25);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreativeReviewPreviewResponse | null>(
    null,
  );

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await creativeReviewApi.preview(orgUid, {
        client_uid: clientUid,
        // Only pass the edited prompt when it's valid; otherwise let the
        // backend resolve the saved/default prompt.
        brief_prompt_override: draftValid ? draft : null,
        sample,
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const briefs = useMemo(
    () =>
      (result?.brief_results ?? []).map(
        (b) => b as Record<string, unknown>,
      ),
    [result],
  );

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-slate-700">
        Simulate (dry-run — nothing is saved)
      </h2>
      <div className="flex items-end gap-3">
        <label className="text-xs text-slate-600">
          Sample size
          <input
            type="number"
            min={1}
            max={200}
            value={sample}
            onChange={(e) => setSample(Number(e.target.value) || 1)}
            className="mt-1 block w-24 px-2 py-1.5 text-xs border border-slate-300 rounded-lg"
          />
        </label>
        <button
          onClick={run}
          disabled={running}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-slate-900 text-white disabled:opacity-40"
        >
          {running ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Play className="w-3.5 h-3.5" />
          )}
          Run preview
        </button>
        <span className="text-[11px] text-slate-400">
          {draftValid
            ? 'uses the edited prompt'
            : 'edited prompt invalid — uses saved/default'}
        </span>
      </div>

      {error && <ErrorBanner error={error} />}

      {result && (
        <div className="space-y-3">
          <div className="flex gap-4 text-xs text-slate-600">
            <span>
              clusters:{' '}
              <span className="font-semibold">{result.cluster_count}</span>
            </span>
            <span>
              briefs:{' '}
              <span className="font-semibold">{result.brief_count}</span>
            </span>
            <span className="text-slate-400">
              stages: {result.stages_run.join(' → ')}
            </span>
          </div>

          {briefs.length === 0 && (
            <p className="text-xs text-slate-500">
              No briefs synthesized. Check the stage summaries — the corpus
              may have too few concept-bearing creatives, or clusters were
              below the size/concept thresholds.
            </p>
          )}

          <div className="space-y-2">
            {briefs.map((b, i) => {
              const skipped = Boolean(b.skipped);
              return (
                <div
                  key={String(b.cluster_id ?? i)}
                  className={clsx(
                    'rounded-lg border px-3 py-2',
                    skipped
                      ? 'border-slate-200 bg-slate-50'
                      : 'border-slate-200 bg-white',
                  )}
                >
                  {skipped ? (
                    <p className="text-xs text-slate-500">
                      cluster {String(b.cluster_id)} skipped —{' '}
                      {String(b.skip_reason ?? 'n/a')}
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-800">
                          {String(b.name ?? 'Untitled')}
                        </span>
                        <span className="text-[11px] uppercase tracking-wide text-brand-teal">
                          {String(b.objective ?? '')}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-600">
                        {String(b.angle_hypothesis ?? '')}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-400">
                        hook: {String(b.hook_type ?? '—')} · archetype:{' '}
                        {String(b.angle_archetype ?? '—')} · emotion:{' '}
                        {String(b.primary_emotion ?? '—')}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
